import {
	ExplorationApiCategory,
	FashionInspirationProperties,
	ExplorationApi,
	NewExploration,
	ExplorationPinnedImage,
	Exploration,
} from "./types";
import csi from "csi/main";
import axios, { AxiosInstance } from "axios";
import { EXPLORATION_IMAGE_API_URL, EXPLORATIONS_API_URL } from "./constants";

/** API Axios interface that will be used to send request */
interface ApiAxiosInterface {
	/** AxiosInstance that will be used to send requests */
	api: AxiosInstance;
}

/** Fetch data that is needed for Fashion Inspiration API */
export const fetchFashionInspirationProperties =  async <T = FashionInspirationProperties>(): Promise<T> => {
	const response = await axios.get<T>(csi.SERVER_WEB_APP_URL + "/v1/fi/properties");

	return response.data;
};

/** Data that is needed to fetch Fashion Inspiration categories from their API */
interface FetchExplorationCategoriesProps extends ApiAxiosInterface {
	/** Locale that will be used by Fashion Inspiration API to return proper types with translation */
	locale: string;
}

export const fetchExplorationCategories = async <T = ExplorationApiCategory>({ api, locale }: FetchExplorationCategoriesProps): Promise<T> => {
	const response = await api.get<T>(
		`${EXPLORATIONS_API_URL}categories`,
		{
			params: { locale },
		},
	);

	return response.data;
};

/** Data that is needed to fetch Fashion Inspiration Explorations from their API */
interface FetchExplorationsProps extends ApiAxiosInterface {
	/** Fashion Inspiration uses this id to identify PLM node, routerStore.locationUrl is used as node id here */
	tenantReferenceId: string;
}
export const fetchExplorationsData = async <T = ExplorationApi[]>({ api, tenantReferenceId }: FetchExplorationsProps): Promise<T> => {
	const response = await api.get<T>(
		EXPLORATIONS_API_URL,
		{
			params: {
				tenant_reference_id: tenantReferenceId,
			},
		},
	);

	return response.data;
};

/** Data that is needed to create new Exploration via Fashion Inspiration API */
interface CreateNewExplorationProps extends ApiAxiosInterface {
	/** Data that is needed to create new Exploration via Fashion Inspiration API */
	data: NewExploration;
}

export const createNewExploration = async <T=ExplorationApi>({ api, data }: CreateNewExplorationProps): Promise<T> => {
	const response = await api.post<T>(EXPLORATIONS_API_URL, data);

	return response.data;
};

/** Data that is needed to fetch Fashion Inspiration Exploration Pinned Images list from their API */
interface FetchExplorationPinnedImagesDataProps extends ApiAxiosInterface, Pick<Exploration, "id"> {}

export const fetchExplorationPinnedImagesData = async <T = ExplorationPinnedImage[]>({ api, id }: FetchExplorationPinnedImagesDataProps): Promise<T> => {
	const response = await api.get<T>(`${EXPLORATIONS_API_URL}${id}/pinned-images`);

	return response.data;
};

/** Data that is needed to update Exploration via Fashion Inspiration API */
interface UpdateExplorationProps extends ApiAxiosInterface, Pick<Exploration, "id"> {
	/** Data that is needed for Exploration update via Fashion Inspiration API */
	data: Pick<Exploration, "name">;
}

export const updateExploration = async <T=ExplorationApi>({ id, api, data }: UpdateExplorationProps): Promise<T> => {
	const response = await api.patch<T>(`${EXPLORATIONS_API_URL}${id}`, data);

	return response.data;
};

/** Data that is needed to delete Fashion Inspiration Exploration via their API */
interface DeleteExplorationProps extends ApiAxiosInterface, Pick<Exploration, "id"> {}

export const deleteExploration = async ({ api, id }: DeleteExplorationProps): Promise<void> => {
	await api.delete(`${EXPLORATIONS_API_URL}${id}`);
};


/** Data that is needed to update Exploration Image data via Fashion Inspiration API */
interface UpdateExplorationImageProps extends ApiAxiosInterface, Pick<ExplorationPinnedImage, "id"> {
	/** Data that is needed for Exploration Image data update via Fashion Inspiration API */
	data: Pick<ExplorationPinnedImage, "is_pinned">;
}

export const updateExplorationImage = async <T=ExplorationPinnedImage>({ id, api, data }: UpdateExplorationImageProps): Promise<T> => {
	const response = await api.patch<T>(`${EXPLORATION_IMAGE_API_URL}${id}`, data);

	return response.data;
};
